<template>
  <div class="settings-page">
    <router-link class="breadcrumb" to="/admin-pages">
      <span class="breadcrumb-icon">&larr;</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>

    <div class="page-header">
      <div>
        <p class="eyebrow">{{ $adminT('adminAll.user_settings_b9dfef', 'USER SETTINGS') }}</p>
        <h1>{{ $adminT('adminAll.user_settings_18a849', 'User Settings') }}</h1>
        <p class="lede">
          {{ $adminT('adminAll.search_organizer_users_and_manage_coreevent_verification_p_4ffaa6', 'Search organizer users and manage CoreEvent verification, publish access, account type, and event quota.') }}
        </p>
      </div>
      <div class="summary">
        <span class="summary-count">{{ users.length || '-' }}</span>
        <span class="summary-label">{{ $adminT('adminAll.found_a497e9', 'FOUND') }}</span>
      </div>
    </div>

    <div class="card">
      <p class="section-title">{{ $adminT('adminAll.find_user_c32267', 'Find user') }}</p>
      <div class="filters-grid">
        <div class="field">
          <label for="admin-user-search">{{ $adminT('adminAll.name_or_email_994e92', 'Name or email') }}</label>
          <input
            id="admin-user-search"
            v-model.trim="filters.search"
            type="search"
            :placeholder="$adminT('adminAll.search_by_first_name_last_name_or_email_1fbe1b', 'Search by first name, last name, or email')"
            @keyup.enter="searchUsers"
          >
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.organization_519255', 'Organization') }}</label>
          <v-select
            v-model="selectedOrganization"
            :options="organizations"
            :filterable="true"
            label="legalName"
            :placeholder="$adminT('adminAll.filter_by_organization_3b6063', 'Filter by organization')"
          >
            <template #option="{ legalName, publicName, slug }">
              <div class="select-option">
                <strong>{{ legalName }}</strong>
                <span>{{ publicName || '-' }} / {{ slug || '-' }}</span>
              </div>
            </template>
            <template #selected-option="{ legalName, publicName, slug }">
              <div class="select-option">
                <strong>{{ legalName }}</strong>
                <span>{{ publicName || '-' }} / {{ slug || '-' }}</span>
              </div>
            </template>
          <template slot="no-options">{{ $adminT('adminAll.sorry_no_matching_options_f0c837', 'Sorry, no matching options.') }}</template></v-select>
        </div>
        <div class="filter-actions">
          <main-button
            class=""
            flat
            :disabled="loading"
            @click.native="clearFilters"
          >
            {{ $adminT('adminAll.clear_719ea3', 'Clear') }}
          </main-button>
          <main-button
            class=""
            depressed
            :loading="loading"
            :disabled="loading"
            @click.native="searchUsers"
          >
            {{ $adminT('adminAll.search_users_1bd622', 'Search users') }}
          </main-button>
        </div>
      </div>
      <div v-if="loadError" class="alert error">{{ adminAllMessage(loadError) }}</div>
    </div>

    <div class="content-grid">
      <div class="card">
        <p class="section-title">{{ $adminT('adminAll.users_57f2b1', 'Users') }}</p>
        <div v-if="loading" class="empty-state">{{ $adminT('adminAll.loading_users_37282b', 'Loading users...') }}</div>
        <div v-else-if="!users.length" class="empty-state">{{ $adminT('adminAll.no_users_found_e611ef', 'No users found.') }}</div>
        <template v-else>
          <button
            v-for="user in users"
            :key="user.id"
            type="button"
            class="user-row"
            :class="{ 'user-row--selected': selectedUser && selectedUser.id === user.id }"
            @click="selectUser(user)"
          >
            <span>
              <strong>{{ user.firstName }} {{ user.lastName }}</strong>
              <small>{{ user.email }}</small>
            </span>
            <span class="user-meta">{{ adminAllLabel(user.type) }} {{ $adminT('adminAll.quota_12d085', '/ quota') }} {{ user.eventQuota }}</span>
          </button>
        </template>
      </div>

      <div class="card">
        <p class="section-title">{{ $adminT('adminAll.selected_user_483068', 'Selected user') }}</p>
        <div v-if="!selectedUser" class="empty-state">{{ $adminT('adminAll.select_a_user_to_edit_settings_aff632', 'Select a user to edit settings.') }}</div>
        <template v-else>
          <div class="details-grid">
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.name_709a23', 'Name') }}</p>
              <p class="detail-value">{{ selectedUser.firstName }} {{ selectedUser.lastName }}</p>
            </div>
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.email_84add5', 'Email') }}</p>
              <p class="detail-value">{{ selectedUser.email }}</p>
            </div>
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.organizations_076052', 'Organizations') }}</p>
              <p class="detail-value">{{ organizationSummary(selectedUser.organizations) }}</p>
            </div>
            <div class="detail-card">
              <p class="detail-label">{{ $adminT('adminAll.account_type_144b49', 'Account type') }}</p>
              <p class="detail-value">{{ adminAllLabel(selectedUser.type) }}</p>
            </div>
          </div>

          <div class="settings-grid">
            <div class="field checkbox-field">
              <label>
                <input v-model="form.isVerifiedByCoreEvent" type="checkbox">
                {{ $adminT('adminAll.verified_by_coreevent_fd6449', 'Verified by CoreEvent') }}
              </label>
            </div>
            <div class="field checkbox-field">
              <label>
                <input v-model="form.allowPublishEventsToLanding" type="checkbox">
                {{ $adminT('adminAll.allow_publish_db7102', 'Allow publish') }}
              </label>
            </div>
            <div v-if="selectedUser.type === 'TRIAL'" class="field checkbox-field">
              <label>
                <input v-model="form.convertTrialToRegular" type="checkbox">
                {{ $adminT('adminAll.change_trial_to_regular_30dbed', 'Change TRIAL to REGULAR') }}
              </label>
            </div>
            <div class="field">
              <label for="admin-user-quota">{{ $adminT('adminAll.event_quota_37b9a3', 'Event quota') }}</label>
              <input
                id="admin-user-quota"
                v-model.number="form.eventQuota"
                type="number"
                min="0"
                step="1"
              >
            </div>
          </div>

          <div class="actions">
            <main-button class="" flat :disabled="saving" @click.native="resetForm">
              {{ $adminT('adminAll.reset_44c57a', 'Reset') }}
            </main-button>
            <main-button
              class=""
              depressed
              :loading="saving"
              :disabled="saving"
              @click.native="saveSettings"
            >
              {{ $adminT('adminAll.save_user_settings_d6b784', 'Save user settings') }}
            </main-button>
          </div>
          <div v-if="saveError" class="alert error">{{ adminAllMessage(saveError) }}</div>
          <div v-if="success" class="alert success">{{ adminAllMessage(success) }}</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import vSelect from './AdminSelect.vue'
import 'vue-select/dist/vue-select.css'

import { getAdminOrganizations, searchAdminUsers, updateAdminUserSettings } from '@/http'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'user-settings',
  components: {
    'v-select': vSelect,
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadError: null,
      saveError: null,
      success: null,
      users: [],
      organizations: [],
      selectedOrganization: null,
      selectedUser: null,
      filters: {
        search: '',
      },
      form: {
        isVerifiedByCoreEvent: false,
        allowPublishEventsToLanding: false,
        convertTrialToRegular: false,
        eventQuota: 0,
      },
    }
  },
  watch: {
    selectedOrganization() {
      this.success = null
      this.loadError = null
    },
  },
  created() {
    this.loadOrganizations()
    this.searchUsers()
  },
  methods: {
    async loadOrganizations() {
      try {
        const { data } = await getAdminOrganizations()
        this.organizations = (data && data.organizations) || []
      } catch (err) {
        this.loadError =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load organizations.')
      }
    },
    async searchUsers() {
      this.loading = true
      this.loadError = null
      this.success = null
      try {
        const { data } = await searchAdminUsers({
          search: this.filters.search,
          organizationId: this.selectedOrganization && this.selectedOrganization.id,
        })
        this.users = (data && data.users) || []
        if (this.selectedUser) {
          const selectedUser = this.users.find(user => user.id === this.selectedUser.id)
          this.selectedUser = selectedUser || null
          if (this.selectedUser) {
            this.resetForm()
          }
        }
      } catch (err) {
        this.loadError =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load users.')
      } finally {
        this.loading = false
      }
    },
    clearFilters() {
      this.filters.search = ''
      this.selectedOrganization = null
      this.searchUsers()
    },
    selectUser(user) {
      this.selectedUser = user
      this.saveError = null
      this.success = null
      this.resetForm()
    },
    resetForm() {
      if (!this.selectedUser) {
        return
      }
      this.form = {
        isVerifiedByCoreEvent: Boolean(this.selectedUser.isVerifiedByCoreEvent),
        allowPublishEventsToLanding: Boolean(
          this.selectedUser.flags && this.selectedUser.flags.allowPublishEventsToLanding,
        ),
        convertTrialToRegular: false,
        eventQuota: Number(this.selectedUser.eventQuota || 0),
      }
      this.saveError = null
      this.success = null
    },
    async saveSettings() {
      if (!this.selectedUser) {
        return
      }

      this.saveError = null
      this.success = null

      if (!Number.isInteger(Number(this.form.eventQuota)) || Number(this.form.eventQuota) < 0) {
        this.saveError = this.adminAllNotice('Event quota must be a non-negative integer.')

        return
      }

      this.saving = true
      try {
        const payload = {
          isVerifiedByCoreEvent: Boolean(this.form.isVerifiedByCoreEvent),
          allowPublishEventsToLanding: Boolean(this.form.allowPublishEventsToLanding),
          eventQuota: Number(this.form.eventQuota),
        }

        if (this.selectedUser.type === 'TRIAL' && this.form.convertTrialToRegular) {
          payload.type = 'REGULAR'
        }

        const { data } = await updateAdminUserSettings(this.selectedUser.id, payload)

        const updatedUser = (data && data.user) || null
        if (updatedUser) {
          this.users = this.users.map(user => (user.id === updatedUser.id ? updatedUser : user))
          this.selectedUser = updatedUser
          this.resetForm()
        }

        this.success = this.adminAllNotice('User settings saved.')
      } catch (err) {
        this.saveError =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to save user settings.')
      } finally {
        this.saving = false
      }
    },
    organizationSummary(organizations) {
      if (!organizations || !organizations.length) {
        return '-'
      }

      return organizations
        .map(organization => organization.legalName || organization.publicName || organization.slug)
        .join(', ')
    },
  },
}
</script>

<style scoped>
.settings-page {
  padding: 2rem;
}

.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #3d5b9a;
  text-decoration: none;
  margin-bottom: 1.5rem;
}

.breadcrumb-icon {
  font-size: 1rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  color: #7f8da3;
}

.lede {
  margin: 0;
  color: #6b7280;
  max-width: 42rem;
}

.summary {
  min-width: 5rem;
  padding: 1rem;
  border-radius: 1rem;
  background: #f8fafc;
  text-align: center;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.summary-count {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
}

.summary-label {
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  color: #7f8da3;
}

.card {
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.06);
}

.section-title {
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
}

.filters-grid,
.settings-grid,
.details-grid {
  display: grid;
  gap: 1rem;
}

.filters-grid {
  grid-template-columns: minmax(14rem, 1fr) minmax(16rem, 1.4fr) auto;
  align-items: end;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(18rem, 0.9fr) minmax(24rem, 1.4fr);
  gap: 1.5rem;
}

.settings-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 1.5rem;
}

.details-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
}

.field input,
.field select {
  width: 100%;
  min-height: 2.75rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid #dbe3ef;
  border-radius: 0.75rem;
  color: #1f2937;
  background: #ffffff;
}

.filter-actions,
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.actions {
  margin-top: 1.5rem;
}

.checkbox-field {
  justify-content: center;
}

.checkbox-field label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.checkbox-field input {
  width: auto;
  min-height: auto;
}

.user-row {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
}

.user-row + .user-row {
  margin-top: 0.75rem;
}

.user-row--selected {
  border-color: #3d5b9a;
  background: #f7f9fe;
}

.user-row strong,
.user-row small {
  display: block;
}

.user-row small,
.user-meta,
.select-option span {
  color: #6b7280;
}

.user-meta {
  flex: 0 0 auto;
  font-size: 0.85rem;
}

.detail-card {
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #f8fafc;
}

.detail-label {
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.detail-value {
  margin: 0;
  color: #0f172a;
  word-break: break-word;
}

.empty-state {
  padding: 1rem;
  border-radius: 0.75rem;
  background: #f8fafc;
  color: #64748b;
}

.alert {
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
}

.alert.error {
  background: #fef2f2;
  color: #b91c1c;
}

.alert.success {
  background: #ecfdf5;
  color: #047857;
}

.select-option {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.select-option span {
  font-size: 0.8rem;
  white-space: normal;
  word-break: break-word;
}

.card ::v-deep .vs__dropdown-toggle {
  min-height: 2.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid #dbe3ef;
}

.card ::v-deep .vs__selected-options,
.card ::v-deep .vs__selected,
.card ::v-deep .vs__dropdown-option {
  white-space: normal;
}

.card ::v-deep .vs__dropdown-option {
  word-break: break-word;
}

.card ::v-deep .vs__dropdown-menu {
  border-radius: 0.75rem;
  border: 1px solid #dbe3ef;
}

@media (max-width: 1200px) {
  .filters-grid,
  .content-grid,
  .details-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .settings-page {
    padding: 1.25rem;
  }

  .card {
    padding: 1.25rem;
  }

  .page-header {
    flex-direction: column;
  }

  .summary {
    align-self: flex-start;
  }
}

@media (max-width: 640px) {
  .settings-page {
    padding: 0.875rem;
  }

  .card {
    padding: 1rem;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions,
  .actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
